// Search/terminology highlights wrap text; only translation tag images count as
// one character. Count Unicode code points to match PHP's mb_substr offsets.
const getSourceNodes = (root) => Array.from(root.childNodes).flatMap((node) => {
	if (node.nodeType === 3 || node.nodeName === 'IMG') return [node];
	return getSourceNodes(node);
});

export const getSourceCaretPosition = (editor, selection = window.getSelection()) => {
	if (!selection?.rangeCount) return 0;
	const range = selection.getRangeAt(0).cloneRange();
	if (!editor.contains(range.endContainer)) return 0;
	range.setStart(editor, 0);
	return getSourceNodes(range.cloneContents()).reduce((length, node) =>
		length + (node.nodeType === 3 ? Array.from(node.nodeValue).length : 1), 0
	);
};

export const canSplitSourceAtPosition = (editor, position) => {
	let length = 0;
	let openGroups = 0;
	for (const node of getSourceNodes(editor)) {
		if (length >= position) break;
		if (node.nodeType === 3) {
			length += Array.from(node.nodeValue).length;
		} else {
			length++;
			if (node.getAttribute('data-tag') === 'g') {
				if (node.getAttribute('data-type') === 'o') openGroups++;
				if (node.getAttribute('data-type') === 'c') openGroups--;
			}
		}
	}
	return openGroups === 0;
};

export const getLangCodeForChrome = (targetLang) => {
	if (!targetLang) {
		return '';
	}

	if (targetLang === 'en-gb') {
		return 'en-GB';
	}

	return targetLang.split('-')[0];
};

export const nextTick = (fn) => {
	Promise.resolve().then(fn);
};

export function getMultipleIndexes(
	sentence,
	_word,
	_case_sensitive,
	_whole_words,
	_regex_search,
	_multiline,
	replaces
) {
	if (replaces) {
		for (const [key, value] of Object.entries(replaces)) {
			value.map(i => {
				sentence = sentence.replaceAll(i, key);
			});
		}
	}

	// Function to remove diacritics (accents) and special characters
	function normalizeText(text) {
		return text.normalize('NFKD').
			replace(/[\u0300-\u036f]/g, '') // Remove accents
			.replace(/’/g, '\''); // Convert curved apostrophes to straight
	}

	// Normalize both the sentence and the search word
	const normalizedSentence = normalizeText(sentence);
	const normalizedWord = normalizeText(_word);

	/*besides '_word' param, others are flags (0|1)*/
	const flags = ['g', _case_sensitive ? '' : 'i', _multiline ? 'm' : ''];
	let regex = _regex_search ?
		String.raw`${normalizedWord}` :
		escapeRegExp(normalizedWord);
	if (_whole_words && normalizedWord) regex = String.raw`\b${regex}\b`;

	var _pos = [],
		_chunk;

	try {
		var _re = new RegExp(regex, flags.join(''));

		while (true) {
			_chunk = _re.exec(normalizedSentence);
			if (_chunk == null) break;
			_pos.push(_chunk['index']);
			_re.lastIndex = _chunk['index'] + 1;
		}
	} catch (e) {
	}

	return _pos;
}

//#region Undo Redo
export function pushToUndo(state, arSegments) {
	const segmentsToChange = arSegments.map((S) => {
		return { ...S };
	});

	if (segmentsToChange.length > 0) {
		state = {
			...state,
			undoStack: [[...segmentsToChange], ...state.undoStack]
		};
	}

	return state;
}

//#endregion

export const forceRender = (state) => {
	state.lastUpdate = +new Date();
	return state;
};

export const placeCaretAtEnd = (el) => {
	if (!el) return;

	if (
		typeof window.getSelection != 'undefined' &&
		typeof document.createRange != 'undefined'
	) {
		var range = document.createRange();
		range.selectNodeContents(el);
		range.collapse(false);
		var sel = window.getSelection();
		sel.removeAllRanges();
		sel.addRange(range);
	} else if (typeof document.body.createTextRange != 'undefined') {
		var textRange = document.body.createTextRange();
		textRange.moveToElementText(el);
		textRange.collapse(false);
		textRange.select();
	}
};

export const insertTextAtCursor = (
	text,
	cursorOffset = 1,
	selectionExternal = null
) => {
	if (window?.activeEditor) {
		// Toolbar and symbol-dialog clicks move focus away from Quill. In that
		// state getSelection() is null (or may already be reset), so retain the
		// last range captured by the editor and insert at that original range.
		const selection = selectionExternal
			?? window.activeEditor.getSelection()
			?? window.lastActiveRange;

		//if no focus, return
		if (selection) {
			const range = { ...selection };
			window.activeEditor.insertText(range.index, text, 'user');

			// Check if there's selected text, instead of just cursor at position
			// and delete selection to create a replacing effect
			if (range.length > 0) {
				window.activeEditor.deleteText(
					range.index + text.length,
					range.length,
					'user'
				);
			}
			//move cursor
			nextTick(() => {
				window.activeEditor.focus();
				window.activeEditor.setSelection(range.index + cursorOffset, 0);
			});

			return;
		}
	}

	const activeElement = document.activeElement;
	let valueSet;

	if (activeElement instanceof HTMLInputElement) {
		valueSet = Object.getOwnPropertyDescriptor(
			window.HTMLInputElement.prototype,
			'value'
		).set;
	}
	if (activeElement instanceof HTMLTextAreaElement) {
		valueSet = Object.getOwnPropertyDescriptor(
			window.HTMLTextAreaElement.prototype,
			'value'
		).set;
	}

	if (
		!valueSet ||
		activeElement.selectionStart == null ||
		activeElement.selectionEnd == null
	)
		return;

	const selectionStart = activeElement.selectionStart;
	const selectionEnd = activeElement.selectionEnd;

	valueSet.call(
		activeElement,
		activeElement.value.slice(0, selectionStart) +
		text +
		activeElement.value.slice(selectionEnd)
	);

	activeElement.dispatchEvent(new Event('input', { bubbles: true }));

	activeElement.selectionStart = activeElement.selectionEnd =
		selectionStart + text.length;
};

export const removeTextAtCursor = (cursorOffset = 1) => {
	if (window?.activeEditor) {
		const selection = window.activeEditor.getSelection();

		if (selection?.index - cursorOffset >= 0) {
			window.activeEditor.deleteText(
				selection?.index - cursorOffset,
				cursorOffset,
				'user'
			);
		}
	}
};

// export const insertTextAtCursor = (text) => {
// 	var sel, range, html;
// 	sel = window.getSelection();
// 	range = sel.getRangeAt(0);
// 	range.deleteContents();
// 	var textNode = document.createTextNode(text);
// 	range.insertNode(textNode);
// 	range.setStartAfter(textNode);
// 	sel.removeAllRanges();
// 	sel.addRange(range);
// };

export const insertHtmlAtCaret = (html) => {
	let sel, range;

	//window.activeEditor.focus();

	sel = window.getSelection();
	if (sel.getRangeAt && sel.rangeCount) {
		range = sel.getRangeAt(0);
		range.deleteContents();

		let el = document.createElement('div');
		el.innerHTML = html;

		let frag = document.createDocumentFragment(),
			node,
			lastNode;
		while ((node = el.firstChild)) {
			lastNode = frag.appendChild(node);
		}
		range.insertNode(frag);

		//Preserve the selection
		if (lastNode) {
			range = range.cloneRange();
			range.setStartAfter(lastNode);
			range.collapse(true);
			sel.removeAllRanges();
			sel.addRange(range);
		}

		return window.activeEditor.innerHTML;
	}
};

export const escapeRegExp = (string = '') => {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const isNumeric = (n) => {
	return !isNaN(parseFloat(n)) && isFinite(n);
};

export const preventBrowserReload = () => {
	window.onbeforeunload = function() {
		return 'There are unsaved segments. Leave Page?';
	};
};

export const allowBrowserReload = () => {
	window.onbeforeunload = null;
};

export const isLowerCase = (str) => {
	return str == str.toLowerCase() && str != str.toUpperCase();
};

export const selectionToggleCase = (e, dispatch) => {
	e.preventDefault();

	const sel = window.getSelection();
	const elSelection = sel?.anchorNode?.parentElement?.closest('.gridRow');
	const isInQuill = !!(sel?.anchorNode?.parentElement?.closest?.('.ql-editor'));
	const qSel = isInQuill && window?.activeEditor?.getSelection
		? window.activeEditor.getSelection()
		: null;

	if (!(sel?.getRangeAt && sel.rangeCount)) return;

	const range = sel.getRangeAt(0);
	const contents = range.cloneContents().childNodes;
	const frag = document.createDocumentFragment();

	// Cycle: Upper -> Lower -> Each Word -> Sentence -> Upper ...
	// The current step is whichever transform would be a no-op on the
	// selection's current plain text -- reusing the same tag/accent-aware
	// transforms the dedicated buttons use, so detection can't drift out of
	// sync with what they actually do.
	const CASES = ['upper', 'lower', 'eachWord', 'sentence'];
	const transformForCase = (caseName) => {
		switch (caseName) {
			case 'upper':
				return toUpperCaseExceptTags;
			case 'lower':
				return toLowerCaseExceptTags;
			case 'eachWord':
				return capitalizeExceptTags;
			case 'sentence':
			default:
				return toSentenceCaseExceptTags;
		}
	};

	const plainSelected = sel.toString() ?? '';
	const currentIdx = CASES.findIndex(
		(c) => transformForCase(c)(plainSelected) === plainSelected
	);
	const nextCase = CASES[(currentIdx >= 0 ? currentIdx + 1 : 0) % CASES.length];

	range.deleteContents();
	applyTransformAcrossNodes(contents, transformForCase(nextCase));
	contents.forEach((itm) => {
		frag.appendChild(itm.cloneNode(true));
	});
	range.insertNode(frag);

	if (isInQuill && window?.activeEditor && qSel) {
		setTimeout(() => {
			try {
				window.activeEditor.setSelection(qSel.index, qSel.length);
			} catch (e) {
			}
		}, 0);
	}

	if (
		isInQuill
		&& window.activeEditor
		&& document.activeElement
		&& elSelection
	) {
		setTimeout(() => {
			dispatch({
				type: 'traEditor/updateSegmentText',
				payload: {
					segID: elSelection.getAttribute('data-segid'),
					translationText: window.activeEditor.getText(0,
						window.activeEditor.getLength() - 1)
				}
			});
		}, 1);
	}
};

// Placeholder standing in for a tag/embed node while its surrounding text is
// processed as one continuous string, so a tag splitting a word or sentence
// across separate text nodes doesn't break word/sentence-boundary detection.
const NODE_PLACEHOLDER = String.fromCharCode(0xE000);

// Runs transformFn across every text node in contents as if they were one
// continuous string (tags become NODE_PLACEHOLDER for the duration), then
// splices the transformed text back into each original text node by length.
function applyTransformAcrossNodes(contents, transformFn) {
	let combined = '';
	contents.forEach((itm) => {
		combined += itm.nodeType === 3 ? itm.textContent : NODE_PLACEHOLDER;
	});

	const transformed = transformFn(combined);

	let cursor = 0;
	contents.forEach((itm) => {
		if (itm.nodeType === 3) {
			const len = itm.textContent.length;
			itm.textContent = transformed.substring(cursor, cursor + len);
			cursor += len;
		} else {
			cursor += NODE_PLACEHOLDER.length;
		}
	});
}

export const toSentenceCase = (e) => {
	e.preventDefault();

	let sel = window.getSelection();
	const elSelection = sel.anchorNode.parentElement.closest('.gridRow');
// Remember current Quill selection to restore highlight after mutation
	const isInQuill = !!(sel?.anchorNode?.parentElement?.closest?.('.ql-editor'));
	const qSel = isInQuill && window?.activeEditor?.getSelection
		? window.activeEditor.getSelection()
		: null;
	let range = sel.getRangeAt(0);
	let contents = range.cloneContents().childNodes;
	let frag = document.createDocumentFragment();

	if (sel.getRangeAt && sel.rangeCount) {

		range.deleteContents();
		applyTransformAcrossNodes(contents, toSentenceCaseExceptTags);
		contents.forEach(function(itm) {
			frag.appendChild(itm.cloneNode(true));
		});

		range.insertNode(frag);
		// Restore original selection (keep only that part selected)
		if (isInQuill && window?.activeEditor && qSel) {
			setTimeout(() => {
				try {
					window.activeEditor.setSelection(qSel.index, qSel.length);
				} catch (e) {
				}
			}, 0);
		}
	}
};

function toSentenceCaseExceptTags(str) {
	return str.replace(/([^<.!?]+[.!?]?)\s*/g, function(match) {
		let i = 0;
		const len = match.length;
		while (i < len && (match[i] === '<' || match[i] === NODE_PLACEHOLDER || /\s/.test(match[i]))) {
			i++;
		}
		if (i >= len) return match;
		return (i > 0 ? match.substring(0, i) : '') + match[i].toUpperCase() +
			match.substring(i + 1).toLowerCase();
	});
}

export const lowerCase = (e) => {
	e.preventDefault();

	let sel = window.getSelection();
	const elSelection = sel.anchorNode.parentElement.closest('.gridRow');
	// Remember current Quill selection to restore highlight after mutation
	const isInQuill = !!(sel?.anchorNode?.parentElement?.closest?.('.ql-editor'));
	const qSel = isInQuill && window?.activeEditor?.getSelection ?
		window.activeEditor.getSelection() :
		null;

	let range = sel.getRangeAt(0);
	let contents = range.cloneContents().childNodes;
	let frag = document.createDocumentFragment();

	if (sel.getRangeAt && sel.rangeCount) {
		//find 1st letter
		var tstChar = sel.toString().match(/\w/);
		let caseFunction = 'upper';

		if (tstChar) caseFunction = isLowerCase(tstChar[0]) ? 'upper' : 'lower';

		range.deleteContents();
		contents.forEach(function(itm) {
			if (itm.nodeType == 3) {
				itm.textContent = toLowerCaseExceptTags(itm.textContent);
			}

			frag.appendChild(itm.cloneNode(true));
		});

		range.insertNode(frag);
		// Restore original selection (keep only that part selected)
		if (isInQuill && window?.activeEditor && qSel) {
			setTimeout(() => {
				try {
					window.activeEditor.setSelection(qSel.index, qSel.length);
				} catch (e) {
				}
			}, 0);
		}
	}
};

function toLowerCaseExceptTags(str) {
	return str.replace(/([^<]+)(<[^>]+>|$)/g, function(_, text, tag) {
		return text.toLowerCase() + (tag || '');
	});
}

export const upperCase = (e) => {
	e.preventDefault();

	let sel = window.getSelection();
	const elSelection = sel.anchorNode.parentElement.closest('.gridRow');
	// Remember current Quill selection to restore highlight after mutation
	const isInQuill = !!(sel?.anchorNode?.parentElement?.closest?.('.ql-editor'));
	const qSel = isInQuill && window?.activeEditor?.getSelection ?
		window.activeEditor.getSelection() :
		null;

	let range = sel.getRangeAt(0);
	let contents = range.cloneContents().childNodes;
	let frag = document.createDocumentFragment();

	if (sel.getRangeAt && sel.rangeCount) {
		//find 1st letter
		var tstChar = sel.toString().match(/\w/);
		let caseFunction = 'upper';

		if (tstChar) caseFunction = isLowerCase(tstChar[0]) ? 'upper' : 'lower';

		range.deleteContents();
		contents.forEach(function(itm) {
			if (itm.nodeType == 3) {
				itm.textContent = toUpperCaseExceptTags(itm.textContent);
			}

			frag.appendChild(itm.cloneNode(true));
		});

		range.insertNode(frag);
		// Restore original selection (keep only that part selected)
		if (isInQuill && window?.activeEditor && qSel) {
			setTimeout(() => {
				try {
					window.activeEditor.setSelection(qSel.index, qSel.length);
				} catch (e) {
				}
			}, 0);
		}
	}
};

function toUpperCaseExceptTags(str) {
	return str.replace(/([^<]+)(<[^>]+>|$)/g, function(_, text, tag) {
		return text.toUpperCase() + (tag || '');
	});
}

// Flips each individual letter's case based on its own current state --
// unlike the other case transforms, this has no notion of word/sentence
// boundaries at all, so it's unaffected by tags splitting text apart.
function toToggleCase(str) {
	const toggle = (s) =>
		s.replace(/./g, (ch) => {
			const lower = ch.toLowerCase();
			const upper = ch.toUpperCase();
			// If not a letter, return as-is
			if (lower === upper) return ch;
			return ch === lower ? upper : lower;
		});
	return str.replace(/([^<]+)(<[^>]+>|$)/g, function(_, text, tag) {
		return toggle(text) + (tag || '');
	});
}

export const toggleCase = (e) => {
	e.preventDefault();

	let sel = window.getSelection();
	const elSelection = sel.anchorNode.parentElement.closest('.gridRow');
	// Remember current Quill selection to restore highlight after mutation
	const isInQuill = !!(sel?.anchorNode?.parentElement?.closest?.('.ql-editor'));
	const qSel = isInQuill && window?.activeEditor?.getSelection ?
		window.activeEditor.getSelection() :
		null;

	let range = sel.getRangeAt(0);
	let contents = range.cloneContents().childNodes;
	let frag = document.createDocumentFragment();

	if (sel.getRangeAt && sel.rangeCount) {
		range.deleteContents();
		contents.forEach(function(itm) {
			if (itm.nodeType == 3) {
				itm.textContent = toToggleCase(itm.textContent);
			}

			frag.appendChild(itm.cloneNode(true));
		});

		range.insertNode(frag);
		// Restore original selection (keep only that part selected)
		if (isInQuill && window?.activeEditor && qSel) {
			setTimeout(() => {
				try {
					window.activeEditor.setSelection(qSel.index, qSel.length);
				} catch (e) {
				}
			}, 0);
		}
	}
};

export const capitalizeEachWord = (e) => {
	e.preventDefault();

	let sel = window.getSelection();
	const elSelection = sel.anchorNode.parentElement.closest('.gridRow');
	// Remember current Quill selection to restore highlight after mutation
	const isInQuill = !!(sel?.anchorNode?.parentElement?.closest?.('.ql-editor'));
	const qSel = isInQuill && window?.activeEditor?.getSelection ?
		window.activeEditor.getSelection() :
		null;

	let range = sel.getRangeAt(0);
	let contents = range.cloneContents().childNodes;
	let frag = document.createDocumentFragment();

	if (sel.getRangeAt && sel.rangeCount) {
		range.deleteContents();
		applyTransformAcrossNodes(contents, capitalizeExceptTags);
		contents.forEach(function(itm) {
			frag.appendChild(itm.cloneNode(true));
		});

		range.insertNode(frag);
		// Restore original selection (keep only that part selected)
		if (isInQuill && window?.activeEditor && qSel) {
			setTimeout(() => {
				try {
					window.activeEditor.setSelection(qSel.index, qSel.length);
				} catch (e) {
				}
			}, 0);
		}
	}
};

function capitalizeExceptTags(str) {
	return str.replace(new RegExp('[\\p{L}0-9_' + NODE_PLACEHOLDER + ']+', 'gu'), function(word) {
		let i = 0;
		while (i < word.length && word[i] === NODE_PLACEHOLDER) {
			i++;
		}
		if (i >= word.length) return word;
		return word.substring(0, i) + word.charAt(i).toUpperCase() + word.substring(i + 1).toLowerCase();
	});
}

function capitaliseEachWord(str) {
	let x = str;
	x = x.toLowerCase().split(' ');
	for (var i = 0; i < x.length; i++) {
		x[i] = x[i].charAt(0).toUpperCase() + x[i].slice(1);
	}
	return x.join(' ');
}

function sentenceCase(str) {
	str = str.toLowerCase();
	str = str.toLowerCase().split(' ');
	str[0] = str[0].charAt(0).toUpperCase() + str[0].slice(1);
	return str.join(' ');
}

export const getSelectionText = () => {
	var text = '';
	if (window.getSelection) {
		text = window.getSelection().toString();
	} else if (document.selection && document.selection.type != 'Control') {
		text = document.selection.createRange().text;
	}
	return text;
};

export const escapeHTML = (str) =>
	str.replace(
		/[&<>"]/g,
		(tag) =>
			({
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				'"': '&quot;'
				// "'": '&#39;',
			}[tag])
	);

export const toggleFormatsToSelection = (formats) => {
	if (!window?.activeEditor || !formats) {
		return;
	}

	let selection = window.activeEditor.getSelection();

	// A double-click's native word selection often includes a trailing (or
	// leading) space - e.g. selecting "computer " (9 chars) instead of
	// "computer" (8). Quill's formatText() silently excludes that space from
	// the inline tag it actually wraps (<sastrong>computer</sastrong> stays
	// 8 chars), but still reports the untrimmed 9-char range as the active
	// selection afterwards. Toggling a *second* format (e.g. Italic right
	// after Bold) then reuses that stale 9-char range: Parchment's
	// Inline#formatAt isolates/wraps against the wrong blot for that extra
	// trailing character (it no longer lines up with the just-created
	// <sastrong> blot's own 8-char length), and the isolate ends up
	// spanning a large chunk of whatever comes after - visually bleeding
	// the new format to the rest of the segment. Trimming the selection to
	// the actual word before formatting keeps it aligned with the tag Quill
	// is about to (or already did) create, so a following toggle isolates
	// only that same word again.
	if (selection && selection.length > 0) {
		const text = window.activeEditor.getText(selection.index, selection.length);
		const leading = text.match(/^\s*/)[0].length;
		const trailing = text.match(/\s*$/)[0].length;
		if (leading + trailing > 0 && leading + trailing < text.length) {
			selection = {
				index: selection.index + leading,
				length: selection.length - leading - trailing,
			};
			window.activeEditor.setSelection(selection.index, selection.length, 'silent');
		}
	}

	const selectionFormats = window.activeEditor.getFormat(selection);

	//console.log(selectionFormats);

	if (formats == 'italic') {
		window.activeEditor.format('saitalic', !selectionFormats?.saitalic, 'user');
	}

	if (formats == 'bold') {
		window.activeEditor.format('sastrong', !selectionFormats?.sastrong, 'user');
	}

	if (formats == 'underline') {
		window.activeEditor.format(
			'saunderline',
			!selectionFormats?.saunderline,
			'user'
		);
	}

	if (formats == 'superscript') {
		window.activeEditor.format('sasup', !selectionFormats?.sasup, 'user');
	}

	if (formats == 'subscript') {
		window.activeEditor.format('sasub', !selectionFormats?.sasub, 'user');
	}

	// Defense in depth: re-assert the (now word-trimmed) range by Quill's
	// own index API after formatting too, in case anything else nudges the
	// native-DOM selection during the format() call.
	if (selection) {
		window.activeEditor.setSelection(selection.index, selection.length, 'silent');
	}
};

export const getSimilarFontByName = (fontName) => {
	return 'sans-serif';
};
